<script setup>
// Menu Meta -> Template (id 1 + e-mails MetaAccess). Dois modos:
// CONSULTAR: seleciona 1+ caixas WhatsApp Cloud e lista os templates de cada
//   uma (ao vivo na Meta), com o nome da caixa e o numero conectado.
// CRIAR: lista SO as caixas com saude ok na Meta (health 200); seleciona 1+
//   e o mesmo template e criado em todas (CreateTemplateModal multi-caixa).
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import InboxTemplatesAPI from 'dashboard/api/inboxTemplates';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import TemplatesTable from '../settings/inbox/components/TemplatesTable.vue';
import CreateTemplateModal from '../settings/inbox/components/CreateTemplateModal.vue';

const store = useStore();
const inboxesGetter = useMapGetter('inboxes/getInboxes');

const mode = ref('consultar');
const showCreateModal = ref(false);

const cloudInboxes = computed(() =>
  (inboxesGetter.value || []).filter(
    i =>
      i.channel_type === 'Channel::Whatsapp' &&
      i.provider === 'whatsapp_cloud' &&
      // DESATIVAR CAIXA (v76): fora das funcoes Template/Disparo do menu Meta
      !i.meta_disabled &&
      // v92: caixas OCULTADAS (Configuracoes -> Ocultar) tambem ficam fora
      !i.hidden
  )
);

// --- CONSULTAR -------------------------------------------------------------
const selectedConsulta = ref({});
const consultaResults = ref([]);
const isConsulting = ref(false);

const consultaSelectedBoxes = computed(() =>
  cloudInboxes.value.filter(i => selectedConsulta.value[i.id])
);

const consultar = async () => {
  if (!consultaSelectedBoxes.value.length) return;
  isConsulting.value = true;
  consultaResults.value = [];
  const boxes = consultaSelectedBoxes.value;
  const results = await Promise.all(
    boxes.map(async box => {
      const item = {
        id: box.id,
        name: box.name,
        phone: box.phone_number || '',
        templates: [],
        error: '',
      };
      try {
        const [tplRes, healthRes] = await Promise.allSettled([
          InboxTemplatesAPI.listTemplates(box.id),
          InboxHealthAPI.getHealthStatus(box.id),
        ]);
        if (tplRes.status === 'fulfilled') {
          item.templates = tplRes.value.data.data || [];
        } else {
          item.error =
            tplRes.reason?.response?.data?.error || tplRes.reason?.message;
        }
        if (healthRes.status === 'fulfilled') {
          item.phone =
            healthRes.value.data.display_phone_number || item.phone;
        }
      } catch (e) {
        item.error = e?.response?.data?.error || e.message;
      }
      return item;
    })
  );
  consultaResults.value = results;
  isConsulting.value = false;
};

// --- CRIAR -------------------------------------------------------------------
// Saude por caixa (v: meta-v1): TODAS as caixas aparecem na lista com o badge
// de saude na frente (verde/amarelo/vermelho pela quality da Meta + limite de
// mensagens); so as saudaveis (health 200) podem ser selecionadas pro lote.
const healthStatus = ref({});
const isLoadingHealth = ref(false);
const selectedCriar = ref({});

const healthyInboxes = computed(() =>
  cloudInboxes.value.filter(i => healthStatus.value[i.id]?.ok)
);

const criarSelectedBoxes = computed(() =>
  healthyInboxes.value.filter(i => selectedCriar.value[i.id])
);

const QUALITY_DOT = {
  GREEN: 'bg-n-teal-9',
  YELLOW: 'bg-n-amber-9',
  RED: 'bg-n-ruby-9',
};
const QUALITY_LABEL = {
  GREEN: 'Saúde alta',
  YELLOW: 'Saúde média',
  RED: 'Saúde baixa',
};

const healthBadge = box => {
  const h = healthStatus.value[box.id];
  if (!h) return { dot: 'bg-n-slate-8', text: 'verificando…', title: '' };
  if (!h.ok)
    return { dot: 'bg-n-ruby-9', text: 'indisponível', title: h.error || '' };
  const q = (h.quality || '').toUpperCase();
  return {
    dot: QUALITY_DOT[q] || 'bg-n-slate-8',
    text: `${QUALITY_LABEL[q] || h.quality || '—'}${h.tier ? ` · ${h.tier}` : ''}`,
    title: '',
  };
};

const loadHealth = async () => {
  isLoadingHealth.value = true;
  await Promise.all(
    cloudInboxes.value.map(async box => {
      try {
        const { data } = await InboxHealthAPI.getHealthStatus(box.id);
        healthStatus.value[box.id] = {
          ok: true,
          phone: data.display_phone_number || box.phone_number || '',
          quality: data.quality_rating || '—',
          tier: data.messaging_limit_tier || '',
        };
      } catch (e) {
        healthStatus.value[box.id] = {
          ok: false,
          error: e?.response?.data?.error || e.message,
        };
      }
    })
  );
  isLoadingHealth.value = false;
};

const enterCriar = () => {
  mode.value = 'criar';
  if (!Object.keys(healthStatus.value).length) loadHealth();
};

const onTemplatesCreated = () => {
  showCreateModal.value = false;
};

onMounted(async () => {
  await store.dispatch('inboxes/get');
  // Badge de saude visivel ja na entrada da pagina (CONSULTAR e CRIAR).
  loadHealth();
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-surface-1 p-8">
    <div class="max-w-6xl w-full mx-auto">
      <div class="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 class="text-heading-1 text-n-slate-12 mb-1">Meta — Template</h1>
          <p class="text-n-slate-11 text-sm">
            Consulte os templates das caixas conectadas ou crie um template em
            uma ou mais caixas de uma vez.
          </p>
        </div>
        <div class="flex gap-2 shrink-0">
          <NextButton
            :variant="mode === 'criar' ? 'solid' : 'outline'"
            label="CRIAR"
            @click="enterCriar"
          />
          <NextButton
            :variant="mode === 'consultar' ? 'solid' : 'outline'"
            label="CONSULTAR"
            @click="mode = 'consultar'"
          />
        </div>
      </div>

      <!-- ==================== CONSULTAR ==================== -->
      <template v-if="mode === 'consultar'">
        <div class="rounded-lg border border-n-weak p-4 mb-6">
          <p class="text-sm font-medium text-n-slate-12 mb-3">
            Caixas de entrada conectadas (WhatsApp Cloud) — selecione uma ou
            mais:
          </p>
          <div class="grid grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
            <label
              v-for="box in cloudInboxes"
              :key="box.id"
              class="flex items-center gap-2 text-sm text-n-slate-12 cursor-pointer"
            >
              <Checkbox v-model="selectedConsulta[box.id]" />
              <span>{{ box.name }}</span>
              <span class="text-xs text-n-slate-10">{{ box.phone_number }}</span>
              <span
                class="flex items-center gap-1 text-xs text-n-slate-11 ml-auto"
                :title="healthBadge(box).title"
              >
                <span
                  :class="`inline-block w-2 h-2 rounded-full ${healthBadge(box).dot}`"
                />
                {{ healthBadge(box).text }}
              </span>
            </label>
          </div>
          <NextButton
            label="Consultar templates"
            :is-loading="isConsulting"
            :disabled="!consultaSelectedBoxes.length"
            @click="consultar"
          />
        </div>

        <div
          v-for="result in consultaResults"
          :key="result.id"
          class="mb-8"
        >
          <h2 class="text-heading-2 text-n-slate-12 mb-1">
            Templates da caixa — {{ result.name }}
          </h2>
          <p class="text-n-slate-11 text-sm mb-3">
            Número conectado na Meta: {{ result.phone || '—' }}
          </p>
          <p v-if="result.error" class="text-sm text-n-ruby-11 mb-3">
            {{ result.error }}
          </p>
          <div
            v-else-if="!result.templates.length"
            class="text-sm text-n-slate-11 py-4 text-center border border-n-weak rounded-lg"
          >
            Nenhum template nesta conta WhatsApp Business.
          </div>
          <TemplatesTable v-else :templates="result.templates" />
        </div>
      </template>

      <!-- ==================== CRIAR ==================== -->
      <template v-else>
        <div class="rounded-lg border border-n-weak p-4">
          <p class="text-sm font-medium text-n-slate-12 mb-1">
            Selecione os números para criar o template em lote:
          </p>
          <p class="text-xs text-n-slate-10 mb-3">
            A saúde da conta na Meta aparece na frente de cada número. Caixas
            com credenciais quebradas ou impedidas pela Meta não podem ser
            selecionadas.
          </p>
          <div v-if="isLoadingHealth" class="text-sm text-n-slate-11 py-4">
            Verificando a saúde das caixas na Meta…
          </div>
          <template v-else>
            <div
              v-if="!cloudInboxes.length"
              class="text-sm text-n-slate-11 py-4"
            >
              Nenhuma caixa WhatsApp Cloud conectada.
            </div>
            <div v-else class="grid grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
              <label
                v-for="box in cloudInboxes"
                :key="box.id"
                class="flex items-center gap-2 text-sm text-n-slate-12"
                :class="
                  healthStatus[box.id]?.ok
                    ? 'cursor-pointer'
                    : 'opacity-60 cursor-not-allowed'
                "
              >
                <Checkbox
                  v-model="selectedCriar[box.id]"
                  :disabled="!healthStatus[box.id]?.ok"
                />
                <span>{{ box.name }}</span>
                <span class="text-xs text-n-slate-10">
                  {{ healthStatus[box.id]?.phone || box.phone_number }}
                </span>
                <span
                  class="flex items-center gap-1 text-xs text-n-slate-11 ml-auto"
                  :title="healthBadge(box).title"
                >
                  <span
                    :class="`inline-block w-2 h-2 rounded-full ${healthBadge(box).dot}`"
                  />
                  {{ healthBadge(box).text }}
                </span>
              </label>
            </div>
            <div class="flex gap-2">
              <NextButton
                label="CRIAR EM LOTE"
                :disabled="!criarSelectedBoxes.length"
                @click="showCreateModal = true"
              />
              <NextButton
                variant="outline"
                color="slate"
                label="Reverificar saúde"
                :is-loading="isLoadingHealth"
                @click="loadHealth"
              />
            </div>
          </template>
        </div>
      </template>

      <CreateTemplateModal
        v-if="showCreateModal"
        :inboxes="criarSelectedBoxes"
        @close="showCreateModal = false"
        @created="onTemplatesCreated"
      />
    </div>
  </div>
</template>
